import gsap, { Sine } from 'gsap';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Constrain, FilterText, Wrapper } from './styles';

import { clamp, forEach } from 'lib/helpers';

import FilterButton from './FilterButton';

import filtersMap from 'components/FiltersModal/filtersMap';
import { DEFAULT, FILTERS_MODAL } from 'components/Scroller/constants';
import { AnalyticsContext } from 'contexts/AnalyticsContext';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { CONTAINERS, FILTER_MODE } from 'contexts/CruiseSearchContext/constants';
import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';
import { SELECTING } from 'contexts/CruiseSelectionContext/constants';
import { filterDictionary } from 'lib/globalization/Dictionary';
import defaultLabels from 'lib/globalization/Dictionary/dictionaries/en.itinerary.json';

import { useFeatureFlags } from '../../hooks/LaunchDarkly/useFeatureFlags';
import { FILTERS_CONTENT, FILTERS_WRAPPER, FILTER_CUSTOM, FILTER_NUMBER_GUEST_KEY } from './constants';

const DRAG_THRESHOLD = 7;

/**
 * Filters
 *
 * @param {object} passedRef - ?
 */

function Filters({ passedRef, searchFilters, updateContainer }) {
	const { openFilters, globalization, filterOptionsDictionary } = useContext(CruiseSearchContext);
	const { showInOrderThePillsOfTheFilters } = useFeatureFlags();

	const { custom = [] } = filterOptionsDictionary || {};

	const { utagGuestClicked } = useContext(AnalyticsContext);

	const { comparisonViewState } = useContext(CruiseSelectionContext);

	const groupLabels = filterDictionary(defaultLabels);
	const defaultLabelFilterBy = groupLabels?.filtersDictionary?.labelFilterby || '';

	const { labelFilterby = defaultLabelFilterBy } = globalization?.dictionaries?.filtersDictionary || {};

	const wrapperRef = useRef();

	const [isSticky, setIsSticky] = useState(false);
	const [classSticky, setClassSticky] = useState('isNoSticky hidden');
	const [firtsLoad, setFirstLoad] = useState(true);

	const offsetX = useRef(0);
	const scrollWidth = useRef(0);
	const wrapperPaddingRight = useRef(0);
	const dragging = useRef(false);
	const dragCount = useRef(0);
	const min = useRef(0);

	const speedX = useRef(0);
	const timestamp = useRef(null);
	const lastX = useRef(null);

	const [filtersToShow, setFiltersToShow] = useState({});

	const onMove = useCallback((event) => {
		const clientX = event?.clientX || (event?.targetTouches && event?.targetTouches[0].clientX) || 0;

		dragCount.current += 1;
		const ignore = scrollWidth.current <= window.innerWidth || dragCount.current < DRAG_THRESHOLD;

		if (timestamp.current === null) {
			timestamp.current = Date.now();
			lastX.current = clientX;

			return;
		}

		const now = Date.now();
		const dt = now - timestamp.current;
		const dx = clientX - lastX.current;
		lastX.current = clientX;

		const direction = dx <= 0 ? -1 : 1;

		if (ignore) {
			offsetX.current += dragCount.current * direction;
			return;
		}

		speedX.current = Math.round((dx / dt) * 100);

		timestamp.current = now;

		dragging.current = true;
		min.current = -(scrollWidth.current - window.innerWidth + wrapperPaddingRight.current);
		let x = clientX - offsetX.current;
		x = clamp(x, min.current, 0);
		gsap.set(wrapperRef.current, { x });
	}, []);

	const onDownOrStart = useCallback(
		(event) => {
			gsap.killTweensOf(wrapperRef.current);
			const x = gsap.getProperty(wrapperRef.current, 'x');
			const clientX = event?.clientX || event?.targetTouches[0].clientX;
			offsetX.current = clientX - x;

			scrollWidth.current = wrapperRef.current?.scrollWidth || window.innerWidth;
			wrapperPaddingRight.current = gsap.getProperty(wrapperRef.current, 'paddingRight') || 0;
			window.addEventListener('mousemove', onMove);
			window.addEventListener('touchmove', onMove);
		},
		[onMove],
	);

	const onUpOrEnd = useCallback(() => {
		const ignore = scrollWidth.current <= window.innerWidth || dragCount.current < 7;
		if (ignore) {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('touchmove', onMove);
			return;
		}

		dragCount.current = 0;
		dragging.current = false;
		const { x: wrapperX } = wrapperRef.current.getBoundingClientRect();

		const x = clamp(wrapperX + speedX.current, min.current, 0);
		const duration = clamp(1 - Math.abs(speedX.current) * 0.0035, 0.15, 0.7);
		gsap.to(wrapperRef.current, { x, duration, ease: Sine.easeOut });

		window.removeEventListener('mousemove', onMove);
		window.removeEventListener('touchmove', onMove);
	}, [onMove]);

	useEffect(() => {
		window.addEventListener('mouseup', onUpOrEnd);
		window.addEventListener('touchend', onUpOrEnd);
		return () => {
			window.removeEventListener('mouseup', onUpOrEnd);
			window.removeEventListener('touchend', onUpOrEnd);
		};
	}, [onUpOrEnd]);

	const onResize = useCallback(() => {
		const ignore = scrollWidth.current && scrollWidth.current <= window.innerWidth;
		if (ignore) return;
		gsap.to(wrapperRef.current, { x: 0, duration: 0.25 });
	}, []);

	const selectingForCompare = useMemo(() => comparisonViewState === SELECTING, [comparisonViewState]);

	useEffect(() => {
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [onResize]);

	const openFilter = (key) => {
		if (dragging.current) return;
		openFilters(key, FILTER_MODE.SINGLE_FILTER);
	};

	useEffect(() => {
		let newFilters = {};
		if (showInOrderThePillsOfTheFilters) {
			Object.assign(newFilters, { all: filtersMap.all });

			const filters = showInOrderThePillsOfTheFilters.split('|');
			forEach(filters, (item) => {
				const dataFilter = filtersMap[item];

				Object.assign(newFilters, {
					[item]: {
						key: item,
						label: dataFilter.label,
						showSearchBar: dataFilter.showSearchBar,
						component: dataFilter.component,
						ButtonComponent: dataFilter.ButtonComponent,
						labelKey: dataFilter.labelKey,
						dataTestId: dataFilter.dataTestId,
					},
				});
			});
		} else {
			newFilters = { ...filtersMap };
		}

		setFiltersToShow(newFilters);
	}, [showInOrderThePillsOfTheFilters]);

	useEffect(() => {
		if (searchFilters === DEFAULT) {
			if (!firtsLoad && isSticky) {
				setClassSticky('isNoSticky');
				setTimeout(() => {
					setClassSticky('');
				}, 20);
			}
			if (searchFilters === DEFAULT && !firtsLoad && !isSticky) {
				setClassSticky('isNoSticky');
			}
		}
	}, [isSticky]);

	const handleTransitionEnd = () => {
		if (!isSticky) {
			setClassSticky('isNoSticky hidden');
		}
	};

	useEffect(() => {
		if (searchFilters === DEFAULT && 'IntersectionObserver' in window) {
			const cachedRef = wrapperRef.current;
			setFirstLoad(false);
			const observer = new IntersectionObserver(
				([e]) => {
					setIsSticky(e.intersectionRatio < 1);
					if (updateContainer) {
						updateContainer(e.intersectionRatio >= 1);
					}
				},
				{
					threshold: [1],
				},
			);
			observer.observe(cachedRef);
			return function () {
				observer.unobserve(cachedRef);
			};
		}
	}, []);

	const removeCustomFilter = (obj) => Object.fromEntries(Object.entries(obj).filter(([key]) => !key.includes(FILTER_CUSTOM)));

	const hasCurrentOffers = () => {
		const options = custom?.options || [];
		const containers = options.flatMap((option) => option.containers || []);
		return containers.includes(CONTAINERS.FILTER_CUSTOM);
	};

	const enabledFilters = hasCurrentOffers() ? filtersToShow : removeCustomFilter(filtersToShow);

	const { all: allFilter, ...listFilters } = enabledFilters;

	const AnalyticsFn = {
		[FILTER_NUMBER_GUEST_KEY]: () => {
			utagGuestClicked();
		},
	};

	return (
		<Wrapper
			id={FILTERS_WRAPPER}
			ref={wrapperRef}
			onTouchStart={onDownOrStart}
			onMouseDown={onDownOrStart}
			searchFilters={searchFilters}
			className={[isSticky && 'isSticky', selectingForCompare ? 'compare' : '']}
		>
			<FilterText onTransitionEnd={handleTransitionEnd} className={classSticky}>
				{labelFilterby}:
			</FilterText>
			{allFilter && (
				<FilterButton
					filter={allFilter}
					openClick={(e, key) => {
						e.preventDefault();
						openFilter(key);
					}}
					searchFilters={searchFilters}
				/>
			)}
			<Constrain data-testid="cruise-filter-container" id={FILTERS_CONTENT} ref={passedRef}>
				{Object.values(listFilters).map((filter) => {
					return (
						<FilterButton
							key={filter.key}
							filter={filter}
							openClick={(e, key) => {
								e.preventDefault();
								openFilter(key);
								const fnAnalytic = AnalyticsFn[key];
								if (fnAnalytic) {
									fnAnalytic();
								}
							}}
							searchFilters={searchFilters}
						/>
					);
				})}
			</Constrain>
		</Wrapper>
	);
}

Filters.propTypes = {
	passedRef: PropTypes.object,
	searchFilters: PropTypes.oneOf([DEFAULT, FILTERS_MODAL]),
	updateContainer: PropTypes.func,
};

export default Filters;
